import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Input, Tag, message } from 'antd';
import PublicLayout from '../../layout/PublicLayout';
import MaterialInput from '../../components/common/MaterialInput';
import { getDriveAuthUrl, getDriveStatus } from '../../api/googleDrive';
import ConnectDriverWrapper from './styles';

// Hidden page: /connect-driver
// Step 1: enter the connect password (checked by the API, default 123456)
// Step 2: see the current Google Drive connection and press "Connect Google"
//         -> browser goes to Google consent screen -> Google redirects to
//         api.thelucaso.com/api/v1/google-drive/callback -> API creates the
//         folders, saves google-drive.json and redirects back here.

const SESSION_KEY = 'connectDriverPassword';

function readQuery(search) {
  const params = new URLSearchParams(search);
  return {
    connected: params.get('connected'),
    email: params.get('email'),
    error: params.get('error'),
  };
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(Number(value) || value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

export default function ConnectDriver({ location, history }) {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(SESSION_KEY) || '',
  );
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const loadStatus = useCallback(
    async (pw) => {
      setLoading(true);
      try {
        const data = await getDriveStatus(pw);
        setStatus(data);
        setUnlocked(true);
        sessionStorage.setItem(SESSION_KEY, pw);
      } catch (e) {
        setUnlocked(false);
        sessionStorage.removeItem(SESSION_KEY);
        message.error(e.message || 'Wrong password');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Handle the redirect back from the API callback
  useEffect(() => {
    const q = readQuery(location.search);
    if (q.connected) {
      message.success(`Google Drive connected${q.email ? ` as ${q.email}` : ''}`);
    } else if (q.error) {
      message.error(`Connect failed: ${q.error}`, 8);
    }
    if (q.connected || q.error) {
      history.replace(location.pathname);
    }
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      loadStatus(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!password) return;
    loadStatus(password);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const returnUrl = `${window.location.origin}/connect-driver`;
      const { url } = await getDriveAuthUrl({
        password,
        returnUrl,
        clientId,
        clientSecret,
      });
      window.location.assign(url);
    } catch (e) {
      message.error(e.message || 'Cannot build Google login URL');
      setConnecting(false);
    }
  };

  const handleLock = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setUnlocked(false);
    setStatus(null);
    setPassword('');
  };

  const folders = (status && status.folders) || {};

  return (
    <PublicLayout>
      <ConnectDriverWrapper>
        <div className="title">
          <span>Connect Google Drive</span>
        </div>

        {!unlocked ? (
          <form onSubmit={handleUnlock}>
            <MaterialInput
              placeholder="Connect password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
            />
            <div className="action-div" style={{ marginTop: 30 }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Continue
              </Button>
            </div>
          </form>
        ) : (
          <div>
            <div className="status-card">
              <div className="row">
                <span className="label">Status</span>
                <span className="value">
                  {status && status.connected ? (
                    <Tag color="green">connected</Tag>
                  ) : (
                    <Tag color="red">not connected</Tag>
                  )}
                </span>
              </div>
              <div className="row">
                <span className="label">Account</span>
                <span className="value">{(status && status.email) || '-'}</span>
              </div>
              <div className="row">
                <span className="label">Source</span>
                <span className="value">{status && status.source}</span>
              </div>
              <div className="row">
                <span className="label">Connected at</span>
                <span className="value">{formatDate(status && status.connectedAt)}</span>
              </div>
              <div className="row">
                <span className="label">Token expiry</span>
                <span className="value">{formatDate(status && status.expiryDate)}</span>
              </div>
              <div className="row">
                <span className="label">platformFolderId</span>
                <span className="value">{folders.platformFolderId || '-'}</span>
              </div>
              <div className="row">
                <span className="label">productFolderId</span>
                <span className="value">{folders.productFolderId || '-'}</span>
              </div>
              <div className="row">
                <span className="label">productCategoryFolderId</span>
                <span className="value">{folders.productCategoryFolderId || '-'}</span>
              </div>
              <div className="row">
                <span className="label">customers folder</span>
                <span className="value">{folders.folder || '-'}</span>
              </div>
              <div className="row">
                <span className="label">Redirect URI</span>
                <span className="value">{status && status.redirectUri}</span>
              </div>
            </div>

            <Button type="link" size="small" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Hide' : 'Use another OAuth client'}
            </Button>
            {showAdvanced && (
              <div className="advanced">
                <Input
                  placeholder="OAuth client_id (Web application)"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
                <Input.Password
                  placeholder="OAuth client_secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                />
                <div className="hint">
                  The client must be a &quot;Web application&quot; OAuth client in Google
                  Cloud Console with the Redirect URI above added to its authorized
                  redirect URIs. Leave empty to use the server default.
                </div>
              </div>
            )}

            <div className="action-div" style={{ marginTop: 10 }}>
              <Button
                type="primary"
                icon="google"
                loading={connecting}
                onClick={handleConnect}
              >
                Connect Google
              </Button>
              <Button onClick={() => loadStatus(password)} loading={loading}>
                Refresh status
              </Button>
              <Button type="dashed" onClick={handleLock}>
                Lock
              </Button>
            </div>
            <div className="hint">
              Connecting creates a new &quot;Lucas Uploads&quot; folder with platforms /
              products / productCategories / customers sub folders on the chosen
              Google account and switches the API to it immediately.
            </div>
          </div>
        )}
      </ConnectDriverWrapper>
    </PublicLayout>
  );
}

ConnectDriver.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
};
